import { Link } from "react-router-dom";
import { MapPin, Briefcase, Clock, IndianRupee, GraduationCap, Users, ArrowUpRight, Flame, Star, CalendarClock, UserCheck, Building2 } from "lucide-react";

const CATEGORY_META = {
  hot: { label: "Hot Job", Icon: Flame, cls: "bg-red-50 text-red-600 border-red-100" },
  walkin: { label: "Walk-in", Icon: CalendarClock, cls: "bg-amber-50 text-amber-700 border-amber-100" },
  fresher: { label: "Freshers", Icon: UserCheck, cls: "bg-emerald-50 text-emerald-700 border-emerald-100" },
};

export function relTime(s) {
  if (!s) return "";
  const d = new Date(s);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "Just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  const days = Math.floor(diff / 86400);
  if (days < 30) return `${days}d ago`;
  return d.toLocaleDateString("en-IN", { day: "numeric", month: "short" });
}

export default function JobCard({ job, variant = "default" }) {
  const tag = CATEGORY_META[job.category];
  const isFeatured = job.is_featured;
  const compact = variant === "compact";

  return (
    <article
      data-testid={`job-card-${job.id}`}
      className={`group relative bg-white border rounded-2xl transition-all duration-300 ease-out
        ${isFeatured ? "border-primary/30 ring-1 ring-primary/10" : "border-slate-200"}
        hover:border-primary/50 hover:-translate-y-1 hover:shadow-xl shadow-sm
        ${compact ? "p-5" : "p-6 lg:p-7"}`}
    >
      {/* Top badges */}
      <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
        <div className="flex items-center gap-2 flex-wrap">
          {isFeatured && (
            <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-md bg-gradient-to-r from-primary to-blue-500 text-white shadow-sm">
              <Star className="w-3 h-3 fill-current" /> Featured
            </span>
          )}
          {tag && (
            <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-md border ${tag.cls}`}>
              <tag.Icon className="w-3 h-3" /> {tag.label}
            </span>
          )}
        </div>
        <span className="text-[11px] text-slate-500" data-testid={`job-posted-${job.id}`}>
          {relTime(job.posted_at)}
        </span>
      </div>

      {/* Title + company + dept */}
      <div className="flex items-start gap-3">
        <div className="w-11 h-11 shrink-0 rounded-xl bg-primary/10 group-hover:bg-primary flex items-center justify-center transition-colors">
          <Briefcase className="w-5 h-5 text-primary group-hover:text-white" strokeWidth={1.6} />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="font-heading font-bold text-base sm:text-lg text-slate-900 group-hover:text-primary transition-colors leading-snug line-clamp-2">
            {job.title}
          </h3>
          <div className="mt-1 flex items-center gap-2 text-xs text-slate-500">
            {job.company_name && (
              <span className="inline-flex items-center gap-1 truncate"><Building2 className="w-3.5 h-3.5" /> {job.company_name}</span>
            )}
            {job.company_name && <span className="text-slate-300">·</span>}
            <span className="font-semibold text-primary uppercase tracking-wider">{job.department}</span>
          </div>
        </div>
      </div>

      {/* Meta grid */}
      <div className="mt-5 grid grid-cols-2 gap-x-4 gap-y-2.5 text-xs sm:text-sm text-slate-700">
        <Meta icon={MapPin}>{job.location}</Meta>
        <Meta icon={Clock}>{job.experience}</Meta>
        {job.salary_range && <Meta icon={IndianRupee}>{job.salary_range}</Meta>}
        {job.employment_type && <Meta icon={Briefcase}>{job.employment_type}</Meta>}
        {job.qualification && <Meta icon={GraduationCap}>{job.qualification}</Meta>}
        {job.openings > 0 && (
          <Meta icon={Users}>
            <span className="font-semibold">{job.openings}</span> {job.openings === 1 ? "Opening" : "Openings"}
          </Meta>
        )}
      </div>

      {/* Skills */}
      {job.skills?.length > 0 && !compact && (
        <div className="mt-5 flex flex-wrap gap-1.5">
          {job.skills.slice(0, 4).map((s) => (
            <span key={s} className="text-[11px] font-medium px-2 py-1 rounded-md bg-slate-100 text-slate-700 border border-slate-200">{s}</span>
          ))}
          {job.skills.length > 4 && (
            <span className="text-[11px] font-medium px-2 py-1 rounded-md bg-slate-50 text-slate-500 border border-slate-200">+{job.skills.length - 4}</span>
          )}
        </div>
      )}

      {/* CTA */}
      <div className="mt-6 flex items-center justify-between gap-3 pt-4 border-t border-slate-100">
        <Link
          to={`/jobs/${job.id}`}
          data-testid={`job-card-details-${job.id}`}
          className="text-xs font-semibold text-slate-600 hover:text-primary transition-colors"
        >
          View details
        </Link>
        <Link
          to={`/jobs/${job.id}#apply`}
          data-testid={`job-card-apply-${job.id}`}
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-primary text-white text-xs sm:text-sm font-semibold hover:bg-primary-hover transition-colors shadow-sm"
        >
          Apply Now <ArrowUpRight className="w-3.5 h-3.5" />
        </Link>
      </div>
    </article>
  );
}

function Meta({ icon: Icon, children }) {
  return (
    <span className="inline-flex items-start gap-1.5 min-w-0">
      <Icon className="w-3.5 h-3.5 text-slate-400 shrink-0 mt-0.5" />
      <span className="truncate">{children}</span>
    </span>
  );
}
